import { AdmissionStatusEnum, isShuttleOffer } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking } from "../../Orchestrator/Types/Index";

/**
 * True while the booking has an UNCOMMITTED exchange (change-date or
 * upgrade-to-premium) awaiting payment.
 *
 * Turnit allows only ONE uncommitted modification per booking. An exchange is a
 * modification (originals EXCHANGE_ONGOING, replacements PREBOOKED, nothing
 * committed until the fare difference is paid), and so is
 * `POST /bookings/{id}/booked-offers`. Opening the second discards the first:
 * the replacement offers are DELETED and the originals fall back to FULFILLED.
 *
 * Confirmed live on INT 2026-07-26 and by Transcity: adding an onward shuttle
 * during a pending upgrade silently threw the upgrade away, and the customer
 * was charged AED 6 instead of AED 78. The server had already discarded the
 * offer — no client-side recomputation can recover it.
 *
 * "Additive" in the API docs means "adds to a SETTLED booking", not "adds
 * alongside a pending exchange".
 *
 * NOTE (corrected): an earlier version of this comment argued that a
 * zero-fare-difference change-date is safe because it leaves no
 * EXCHANGE_ONGOING — "verified working at AED 6". That reading was wrong: the
 * train read FULFILLED at that moment because the exchange had ALREADY been
 * rolled back. Callers therefore gate on `isChangeDate` as well as on this
 * predicate (see OnwardTravelCard's `onwardLocked`); this function alone is
 * not sufficient protection during a change-date.
 *
 * See docs/superpowers/specs/2026-07-26-onward-travel-exchange-findings.md
 */
export function hasPendingExchange(booking: IBooking | undefined): boolean {
  return (booking?.bookedOffers || []).some(offer =>
    (offer?.admissions || []).some(
      admission => admission?.status === AdmissionStatusEnum.EXCHANGE_ONGOING
    )
  );
}

/**
 * Admission statuses meaning the passenger genuinely owns this offer right now.
 * (AdmissionStatusEnum has no CONFIRMED — that member is on AncillaryStatusEnum.)
 *
 * The single definition of "owned" for onward products — hydration, the
 * booking-details card and the checkout card all read it, so they cannot drift.
 *
 * PREBOOKED and ON_HOLD are both excluded: neither is paid for. ON_HOLD matches
 * how the rest of the app reads it (BookingOptions and GetBookingToRightDataAction
 * both discount it alongside EXCHANGED when counting live admissions).
 */
export const OWNED_ADMISSION_STATUSES = new Set<string>([
  AdmissionStatusEnum.FULFILLED,
  AdmissionStatusEnum.EXCHANGE_ONGOING,
]);

/**
 * Statuses meaning the travel right is GONE — the complement of "still being
 * carried", which is not the same as the complement of OWNED above.
 *
 * The distinction matters per-admission: PREBOOKED and ON_HOLD are not owned,
 * but the rider is still on the offer and must be counted and priced. Only a
 * terminal status removes them.
 *
 * THREE private copies predate this export: `bookedShuttlesForLeg`,
 * `useHydrateBookedShuttles`, and `sessionRefundLegs` — the last on the live
 * refund path, whose own comment records that the lists must stay identical
 * because a shuttle one side calls live and the other calls history is "a
 * shuttle refunded twice or not at all". All three are left alone as working
 * paths; anyone deduping them must include that one. New callers use this set.
 */
export const TERMINAL_ADMISSION_STATUSES = new Set<string>([
  AdmissionStatusEnum.REFUNDED,
  AdmissionStatusEnum.RELEASED,
  AdmissionStatusEnum.EXCHANGED,
]);

/**
 * True when the booking ALREADY owns a shuttle offer.
 *
 * Backend rule (confirmed 2026-07-26): in the change-date flow, a shuttle may
 * only be surfaced when the original booking already has one — CDT must never
 * offer a shuttle as a NEW purchase. (The upgrade flow concerns only the booked
 * train and shows no onward travel at all.)
 *
 * PREBOOKED is deliberately excluded: an unpaid shuttle from an abandoned
 * session is not owned, and treating it as owned would re-open the purchase
 * path the guard exists to close.
 */
export function hasBookedShuttle(booking: IBooking | undefined): boolean {
  return (booking?.bookedOffers || []).some(offer => {
    // The offer's ADMISSION product, at any index — not products[0], and not
    // "any product". A train offer carrying a shuttle ANCILLARY_ITEM is a train
    // offer. isShuttleOffer is the single shared predicate; see its docstring
    // in globalDefaultEnum.
    if (!isShuttleOffer(offer)) return false;
    return (offer?.admissions || []).some(a => OWNED_ADMISSION_STATUSES.has(a?.status as string));
  });
}
